
import { supabase } from "@/integrations/supabase/client";
import { ThemeConfigItem, ThemeConfig, AppTheme } from "@/types/theme";
import logger from "@/utils/logger";
import OfflineLogger from "@/utils/offlineLogger";
import { networkService } from "@/services/network.service";

const THEME_CACHE_KEY = 'app_theme_config';
const THEME_CACHE_VERSION_KEY = 'app_theme_config_version';
const CURRENT_THEME_ID_KEY = 'app_current_theme_id';

export const getActiveThemes = async (): Promise<AppTheme[]> => {
  try {
    const { data, error } = await supabase
      .from('app_theme')
      .select('*')
      .eq('active', true)
      .order('name');

    if (error) {
      logger.logError('theme.service','Error fetching active themes',{additionalInfo:error});
      return [];
    }

    return (data as AppTheme[]) || [];
  } catch (error) {
    logger.logError('theme.service','Error fetching active themes',{additionalInfo:error});
    return [];
  }
};

export const getThemeConfig = async (themeId?: string): Promise<ThemeConfig> => {
  const cachedThemeId =
    themeId ??
    localStorage.getItem(CURRENT_THEME_ID_KEY) ??
    '11111111-1111-1111-1111-111111111111'; // fallback al tema chiaro

  const cacheKey = `${THEME_CACHE_KEY}_${cachedThemeId}`;

  // If offline, try to return cached theme immediately
  if (!networkService.isOnline()) {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      OfflineLogger.info('theme.service', 'Using cached theme config');
      return JSON.parse(cached) as ThemeConfig;
    }
  }

  try {
    logger.log('ThemeID type:', typeof cachedThemeId);
    logger.log('ThemeID value:', `"${cachedThemeId}"`);

    const { data, error } = await supabase
      .from('app_theme_config')
      .select('*')
      .eq('theme_id', cachedThemeId)
      .order('key');

    logger.log('Fetched data from app_theme_config:', data);

    if (error) {
      throw error;
    }

    if (!data || data.length === 0) {
      OfflineLogger.warn(
        'theme.service',
        `No theme config found for themeId ${cachedThemeId}`
      );
      return {};
    }

    const themeConfig = (data as ThemeConfigItem[]).reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {} as ThemeConfig);

    // Cache for offline usage
    try {
      localStorage.setItem(cacheKey, JSON.stringify(themeConfig));
    } catch (storageError) {
      OfflineLogger.warn('theme.service', 'Failed to cache theme config', storageError);
    }

    logger.log('Parsed theme config:', themeConfig);

    return themeConfig;
  } catch (error: any) {
    if (!networkService.isOnline() || error?.message?.includes('Failed to fetch')) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        OfflineLogger.info('theme.service', 'Returning cached theme after error');
        return JSON.parse(cached) as ThemeConfig;
      }
      OfflineLogger.error('theme.service', error);
      return {};
    }

    logger.logError('theme.service', 'Error fetching theme config', { additionalInfo: error });
    return {};
  }
};

export const setTheme = async (isDark: boolean): Promise<void> => {
  const themeId = isDark
    ? '22222222-2222-2222-2222-222222222222'  // Dark theme ID
    : '11111111-1111-1111-1111-111111111111'; // Light theme ID

  await setThemeId(themeId);

  // Don't call refreshThemeConfig here since ThemeConfigProvider will handle this
};

export const setThemeId = async (themeId: string): Promise<void> => {
  localStorage.setItem(CURRENT_THEME_ID_KEY, themeId);
};

export const refreshThemeConfig = async (): Promise<void> => {
  Object.keys(localStorage)
    .filter(key => key.startsWith(THEME_CACHE_KEY))
    .forEach(key => localStorage.removeItem(key));
  localStorage.removeItem(THEME_CACHE_VERSION_KEY);
  await getThemeConfig();
};

export const getThemeCSSVariables = (theme: ThemeConfig): Record<string, string> => {
  // Convert theme config to CSS variables
  return Object.entries(theme).reduce((acc, [key, value]) => {
    acc[`--${key}`] = value;
    return acc;
  }, {} as Record<string, string>);
};
